import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useDispatch } from 'react-redux';
import { EmailConfiguration } from '~/services/emailAPI/emailService';
import { emailService } from '../services/emailAPI';
import { formatErrorPayload, validateApiResponse } from './utils';

export enum EmailConfigurationErrors {
  GET_EMAIL_CONFIGURATION_ERROR = 'GET_CONFIGURATION_ERROR',
  UPSERT_EMAIL_CONFIGURATION_ERROR = 'UPSERT_CONFIGURATION_ERROR',
  DELETE_EMAIL_CONFIGURATION_ERROR = 'DELETE_CONFIGURATION_ERROR',
}

export const getEmailConfigurationQueryKey = (organizationId: string) => [
  'emailConfiguration',
  organizationId,
];

export function useGetEmailConfigurationQuery({ organizationId }: { organizationId: string }) {
  const dispatch = useDispatch();
  return useQuery(
    getEmailConfigurationQueryKey(organizationId),
    async () => {
      const response = validateApiResponse(
        await emailService.getEmailConfiguration({ organizationId }),
      );
      return response.data;
    },
    {
      enabled: !!organizationId,
      onError: (error: Error) =>
        dispatch(
          formatErrorPayload(error, {
            type: EmailConfigurationErrors.GET_EMAIL_CONFIGURATION_ERROR,
            organizationId,
          }),
        ),
    },
  );
}

export function useEmailConfigurationMutations({ organizationId }: { organizationId: string }) {
  const dispatch = useDispatch();
  const queryClient = useQueryClient();

  return {
    upsertEmailConfiguration: useMutation(
      async ({ configuration }: { configuration: EmailConfiguration }) => {
        const response = validateApiResponse(
          await emailService.upsertEmailConfiguration({ organizationId, configuration }),
        );
        return response.data;
      },
      {
        onSuccess: () => {
          queryClient.invalidateQueries(getEmailConfigurationQueryKey(organizationId));
        },
        onError: (error: Error) => {
          dispatch(
            formatErrorPayload(error, {
              type: EmailConfigurationErrors.UPSERT_EMAIL_CONFIGURATION_ERROR,
            }),
          );
        },
      },
    ),
    deleteEmailConfiguration: useMutation(
      async () => {
        const response = validateApiResponse(
          await emailService.deleteEmailConfiguration({ organizationId }),
        );
        return response.data;
      },
      {
        onSuccess: () => {
          queryClient.invalidateQueries(getEmailConfigurationQueryKey(organizationId));
        },
        onError: (error: Error) => {
          dispatch(
            formatErrorPayload(error, {
              type: EmailConfigurationErrors.DELETE_EMAIL_CONFIGURATION_ERROR,
            }),
          );
        },
      },
    ),
  };
}
